import { MILEAGE_BAND_OPTIONS, mileageBandLabel } from '../../utils/mileageBands';
import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import { SchedulingNoteCell, SchedulingNoteDraft } from './components/SchedulingNoteCell';
import { schedulingFillUpdate } from '../../utils/schedulerNoteFill';
import { WarningConfirmModal } from '../../components/ui/WarningConfirmModal';
import CapacityBoard from './capacity/CapacityBoard';
import BobtailReturnsPanel from './components/BobtailReturnsPanel';
import TabBar from '../../components/TabBar';
import { useState, useCallback, useMemo, useEffect, memo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useMsal } from '@azure/msal-react';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import { TroubleTicketBadge } from '../../components/TroubleTicketBadge';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import DateTimePicker from '../../components/DateTimePicker';
import { useSchedulerData, FieldEditor } from '../../hooks/useSchedulerData';
import { matchLcToCurrentUser } from '../../utils/matchLcToCurrentUser';
import { countOrdersByRegion, buildRegionOptions } from '../../utils/regionOrderCounts';
import { useToast } from '../../contexts/ToastContext';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import { SchedulingLeg, ScheduleStatus, LegScheduleUpdate, SCHEDULE_STATUS_LABELS, SCHEDULE_STATUS_COLORS } from '../../types/scheduling';
import SendToLCModal from './components/SendToLCModal';
import FinalizeModal from './components/FinalizeModal';
import { isSchedulerLegSelectable, missingSchedulingDate, prepullConflict } from '../../utils/prepullRecovery';
import DriverAvailabilityWidget from './components/DriverAvailabilityWidget';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import TerminalSettingsModal from './components/TerminalSettingsModal';
import SchedulerExpandedPanel from './components/SchedulerExpandedPanel';
import SchedulingHistoryDrawer from './components/SchedulingHistoryDrawer';
import MarketConditionsDrawer from './components/MarketConditionsDrawer';
import FilterBar from '../../components/FilterBar';
import api from '../../services/api';
import Notification, { NotificationContainer } from '../../components/Notification';
import { celebrateAchievement } from '../../utils/confetti';
import {
  PaperAirplaneIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
  CheckIcon,
  UsersIcon,
  ShieldCheckIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from '../../components/ui/Tooltip';
import { useIconLegend } from '../../contexts/IconLegendContext';
import { schedulerLegendSections } from '../../utils/schedulerLegendSections';
import { getSchedulingRowClassName } from '../../utils/schedulingRowClass';
import { SCHEDULING_MISSING_WEIGHT_TOOLTIP } from '../../utils/importWeightRule';
import { SCHEDULING_MISSING_HAZ_DOCS_TOOLTIP, legBlockedForMissingHazDocs } from '../../utils/hazmatPaperworkRule';
import schedulingService from '../../services/scheduling.service';
import { SchedulingHistoryRecord } from '../../types/scheduling';
import { useDateDragToFill } from '../../hooks/useDateDragToFill';

// Helper to format dates, returning '-' for null, empty, or epoch dates (0 values in DB)
const formatDate = (date: string | null | undefined): string => {
  if (!date) return '-';
  // Append T00:00:00 to date-only strings to parse as local time, not UTC
  const parsed = new Date(date.includes('T') ? date : date + 'T00:00:00');
  if (isNaN(parsed.getTime())) return '-';
  // Epoch dates (year <= 1970) are invalid - DB stores 0 for empty dates
  if (parsed.getFullYear() <= 1970) return '-';
  return parsed.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
};

// Helper to check if a date is valid (not null, not invalid, not epoch)
const isValidDate = (date: string | null | undefined): boolean => {
  if (!date) return false;
  const parsed = new Date(date.includes('T') ? date : date + 'T00:00:00');
  if (isNaN(parsed.getTime())) return false;
  if (parsed.getFullYear() <= 1970) return false;
  return true;
};

// Robot icon for SYSTEM auto-returned orders (fill follows text color)
const RobotIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="m22.5,10h-.5v-1c0-2.757-2.243-5-5-5h-4v-2c0-.553-.448-1-1-1s-1,.447-1,1v2h-4c-2.757,0-5,2.243-5,5v1h-.5c-.827,0-1.5.673-1.5,1.5v3c0,.827.673,1.5,1.5,1.5h.5v1c0,2.757,2.243,5,5,5h10c2.757,0,5-2.243,5-5v-1h.5c.827,0,1.5-.673,1.5-1.5v-3c0-.827-.673-1.5-1.5-1.5Zm-2.5,7c0,1.654-1.346,3-3,3H7c-1.654,0-3-1.346-3-3v-8c0-1.654,1.346-3,3-3h10c1.654,0,3,1.346,3,3v8Zm-10-6.5c0,.828-.672,1.5-1.5,1.5s-1.5-.672-1.5-1.5.672-1.5,1.5-1.5,1.5.672,1.5,1.5Zm5.5,1.5c-.828,0-1.5-.672-1.5-1.5s.672-1.5,1.5-1.5,1.5.672,1.5,1.5-.672,1.5-1.5,1.5Zm-6.5,4c0,.553-.448,1-1,1h-1c-.552,0-1-.447-1-1s.448-1,1-1h1c.552,0,1,.447,1,1Zm5,0c0,.553-.447,1-1,1h-2c-.552,0-1-.447-1-1s.448-1,1-1h2c.553,0,1,.447,1,1Zm4,0c0,.553-.447,1-1,1h-1c-.553,0-1-.447-1-1s.447-1,1-1h1c.553,0,1,.447,1,1Z"/>
  </svg>
);

// Helper to check if prepull and delivery dates are the same day
const isSamePrepullAndDelivery = (prepull: string | null | undefined, delivery: string | null | undefined): boolean => {
  if (!isValidDate(prepull) || !isValidDate(delivery)) return false;
  // Extract just the date portion (YYYY-MM-DD) to avoid timezone shift issues
  const pDate = prepull!.substring(0, 10);
  const dDate = delivery!.substring(0, 10);
  return pDate === dDate;
};

// Helper to get LFD color class - Red if today or past AND prepull not finalized
const getLfdColorClass = (lfdDate: string | null | undefined, prepullDate: string | null | undefined, status: string | null | undefined): string => {
  if (!isValidDate(lfdDate)) return '';
  // LFD is no longer an issue once prepull is finalized (PENDING_APPT or FINALIZED with prepull date)
  const isPrepullFinalized = isValidDate(prepullDate) && (status === 'PENDING_APPT' || status === 'FINALIZED');
  if (isPrepullFinalized) return '';

  const parsed = new Date(lfdDate!.includes('T') ? lfdDate! : lfdDate! + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (parsed.getTime() <= today.getTime()) return 'text-red-600';
  return '';
};

// Helper to get Cutoff/Per Diem color class - Red if today or past, orange if tomorrow
const getCutoffPerDiemColorClass = (date: string | null | undefined): string => {
  if (!isValidDate(date)) return '';

  const parsed = new Date(date!.includes('T') ? date! : date! + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (parsed.getTime() <= today.getTime()) return 'text-red-600';
  if (parsed.getTime() === tomorrow.getTime()) return 'text-orange-500';
  return '';
};

// Helper to format a Date to YYYY-MM-DD in local timezone (avoids UTC shift from toISOString)
const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const STATUS_OPTIONS: MultiSelectOption[] = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'PLANNING', label: 'Planning' },
  { value: 'PENDING_APPT', label: 'Pending Appt' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'RESCHEDULED', label: 'Rescheduled' },
  { value: 'HOT_ORDER', label: 'Hot Order' },
  { value: 'FINALIZED', label: 'Finalized' },
];

const MOVE_TYPE_OPTIONS: MultiSelectOption[] = [
  { value: 'ALL', label: 'All Move Types' },
  { value: 'IMPORT', label: 'Import' },
  { value: 'EXPORT', label: 'Export' },
  { value: 'DOMESTIC', label: 'Domestic' },
];

const DELIVERY_TYPE_OPTIONS: MultiSelectOption[] = [
  { value: 'LOCAL', label: 'Local' },
  { value: 'OTR', label: 'OTR' },
];

const PREPULL_FILTER_OPTIONS: MultiSelectOption[] = [
  { value: 'all_prepulls', label: 'All Prepulls' },
  { value: 'not_finalized', label: 'Prepulls Not Finalized' },
  { value: 'finalized', label: 'Prepulls Finalized' },
  { value: 'no_prepull', label: 'No Prepull' },
];

const RESTRICTION_OPTIONS: MultiSelectOption[] = [
  {
    value: 'hot',
    label: 'HOT Load',
    icon: <span className="gw-icon gw-icon-hotjar text-brand-red" style={{ fontSize: '16px' }}></span>
  },
  {
    value: 'hazmat',
    label: 'Hazmat',
    icon: <span className="flaticon flaticon-warning text-orange-500" style={{ fontSize: '16px' }}></span>
  },
  {
    value: 'overweight',
    label: 'Overweight',
    icon: <span className="flaticon flaticon-weight text-heading" style={{ fontSize: '16px' }}></span>
  },
  {
    value: 'early',
    label: 'Early',
    icon: <span className="gw-icon gw-icon-early text-heading" style={{ fontSize: '16px' }}></span>
  },
  {
    value: 'triaxle',
    label: 'Triaxle',
    icon: <span className="gw-icon gw-icon-dual-axle text-blue-600" style={{ fontSize: '16px' }}></span>
  },
  {
    value: '93k',
    label: '93K',
    icon: <span className="gw-icon text-heading" style={{ fontSize: '16px' }}>&#xE9E5;</span>
  },
  {
    value: 'swing',
    label: 'Swing',
    icon: <span className="flaticon flaticon-cargo text-purple-600" style={{ fontSize: '18px' }}></span>
  },
  {
    value: 'tank',
    label: 'Tank',
    icon: <span className="flaticon flaticon-oil-tank text-purple-600" style={{ fontSize: '18px' }}></span>
  },
  {
    value: 'daycab',
    label: 'Daycab',
    icon: <span className="gw-icon gw-icon-daycab text-blue-700" style={{ fontSize: '16px' }}></span>
  },
  {
    value: 'reefer',
    label: 'Reefer',
    icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" className="text-blue-500 fill-current"><path d="M11,24c-6.079.117-9.334-7.638-5-11.89V5c.211-6.609,9.791-6.6,10,0v7.11C20.335,16.363,17.077,24.117,11,24ZM11,2A3,3,0,0,0,8,5v7.537a1,1,0,0,1-.332.744A5.018,5.018,0,0,0,11,22a5.018,5.018,0,0,0,3.332-8.719A1,1,0,0,1,14,12.537V5A3,3,0,0,0,11,2Zm0,18a3.007,3.007,0,0,1-1-5.829V12a1,1,0,0,1,2,0v2.171A3.007,3.007,0,0,1,11,20Zm0-4a1,1,0,0,0,0,2A1,1,0,0,0,11,16ZM21,6a3,3,0,0,1,0-6A3,3,0,0,1,21,6Zm0-4a1,1,0,0,0,0,2A1,1,0,0,0,21,2Z"/></svg>
  }
];

// Memoized Date Cell to prevent re-renders when selection changes
interface DateCellProps {
  dateStr: string | null | undefined;
  isLocked: boolean;
  pronumber: number;
  field: 'proposed_prepull_date' | 'proposed_appt_date';
  onUpdate: (pronumber: number, update: LegScheduleUpdate) => void;
  onAutoSelect?: (pronumber: number) => void;
  editor?: FieldEditor | null;
  onFocus?: () => void;
  onBlur?: () => void;
  rowIndex?: number;
  onStartDrag?: (rowIndex: number, field: string, value: string) => void;
}

// Helper functions for user colors (same as footer)
const getUserColor = (name: string): string => {
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500',
    'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
  ];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const getUserBorderColor = (name: string): string => {
  const colors = [
    'ring-blue-500', 'ring-green-500', 'ring-yellow-500', 'ring-red-500',
    'ring-purple-500', 'ring-pink-500', 'ring-indigo-500', 'ring-teal-500',
  ];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const DateCell = memo(function DateCell({
  dateStr,
  isLocked,
  pronumber,
  field,
  onUpdate,
  onAutoSelect,
  editor,
  onFocus,
  onBlur,
  rowIndex,
  onStartDrag,
}: DateCellProps) {
  const dateValue = dateStr ? new Date(dateStr + 'T00:00:00') : null;
  const showHandle = !!dateStr && !isLocked && onStartDrag !== undefined && rowIndex !== undefined;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onFocus={onFocus}
      onBlur={onBlur}
      className={`relative group flex items-center gap-1 ${editor ? `ring-2 ${getUserBorderColor(editor.user_name)} rounded` : ''}`}
    >
      <DateTimePicker
        mode="date"
        value={dateValue}
        onChange={(newDate) => {
          const formatted = newDate instanceof Date
            ? formatDateToISO(newDate)
            : null;
          onUpdate(pronumber, { [field]: formatted });
          if (formatted && onAutoSelect) {
            onAutoSelect(pronumber);
          }
        }}
        inputVariant="compact"
        autoApply
        usePortal
        labelType="none"
        placeholder="Select date"
        disabled={isLocked}
      />
      {editor && (
        <div
          className={`flex-shrink-0 h-5 w-5 rounded-full ${getUserColor(editor.user_name)} text-[0.5rem] text-white flex items-center justify-center font-medium border border-white shadow-sm`}
          title={`${editor.user_name} is editing`}
        >
          {getInitials(editor.user_name)}
        </div>
      )}
      {showHandle && (
        <div
          className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-blue-500 cursor-crosshair opacity-0 group-hover:opacity-100 transition-opacity z-10 rounded-sm"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onStartDrag!(rowIndex!, field, dateStr!);
          }}
        />
      )}
    </div>
  );
});

// Memoized Time Cell - read-only display (time is set by LC, not scheduler)
interface TimeCellProps {
  startTime: string | null | undefined;
  endTime: string | null | undefined;
}

const TimeCell = memo(function TimeCell({
  startTime,
  endTime,
}: TimeCellProps) {
  // Format time string (HH:MM:SS) to military time (HH:MM)
  const formatTime = (timeStr: string | null | undefined): string | null => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const formattedStart = formatTime(startTime);
  const formattedEnd = formatTime(endTime);

  if (!formattedStart && !formattedEnd) {
    return <span className="text-gray-400">-</span>;
  }

  if (formattedStart && formattedEnd) {
    return <span className="text-label">{formattedStart} - {formattedEnd}</span>;
  }

  return <span className="text-label">{formattedStart || formattedEnd}</span>;
});

function SchedulerV1Content() {
  const { accounts } = useMsal();
  const currentUser = accounts[0];
  const { showToast } = useToast();
  const { setDispatchButton } = useDispatchFooter();
  const { openLegend, setLegendSections } = useIconLegend();
  const [activeTab, setActiveTab] = useState('scheduler');
  useFavoriteTab(activeTab, setActiveTab, ['scheduler', 'bobtails', 'capacity-requests', 'reservations']);

  // Send to LC modal state
  const [showSendToLCModal, setShowSendToLCModal] = useState(false);
  const [sendingToLC, setSendingToLC] = useState(false);
  const [legsToSendToLC, setLegsToSendToLC] = useState<SchedulingLeg[]>([]);

  // Finalize modal state
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [legsToFinalize, setLegsToFinalize] = useState<SchedulingLeg[]>([]);

  // Terminal settings modal state
  const [showTerminalSettingsModal, setShowTerminalSettingsModal] = useState(false);
  const [showMarketConditions, setShowMarketConditions] = useState(false);
  // Scheduling history drawer: pro set from a row link, null when opened from the toolbar.
  const [historyDrawer, setHistoryDrawer] = useState<{ open: boolean; pronumber: number | null }>({
    open: false,
    pronumber: null,
  });
  const openHistoryDrawer = useCallback((pronumber: number | null) => {
    setHistoryDrawer({ open: true, pronumber });
  }, []);

  // Order details drawer state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  // History data state (for expanded content sidebar)
  const [historyData, setHistoryData] = useState<Record<number, SchedulingHistoryRecord[]>>({});
  const [historyLoading, setHistoryLoading] = useState<Set<number>>(new Set());

  // Ref shared between drag-to-fill and scheduler data hooks to defer refetches during drag
  const dragIsDeferringRef = useRef(false);

  // Use scheduler data hook
  // Any orders-scope registry field can be added as a column. Rows are LEGS, so a
  // pro with several scheduled legs repeats the same order values down them.
  const fieldSelection = useAdditionalFieldSelection('scheduler');

  const {
    legs,
    filterOptions,
    selectedLegs,
    loading,
    loadingOptions,
    error,
    filters,
    setFilters,
    sendToLC,
    finalizeLegs,
    updateLegSchedule,
    toggleLegSelection,
    selectAllLegs,
    selectLegs,
    deselectLegs,
    clearSelection,
    activeUsers,
    isConnected,
    fieldEditors,
    broadcastFieldFocus,
    broadcastFieldBlur,
    getFieldEditor,
    refetch,
    newPronumbers,
    clearNewPronumbers,
  } = useSchedulerData({
    pageId: 'scheduler',
    userName: currentUser?.name,
    userId: currentUser?.localAccountId,
    userEmail: currentUser?.username,
    enableRealtime: true,
    initialFilters: {
      status: ['ALL'],
    },
    selectableStatuses: ['PLANNING', 'PENDING_APPT', 'RETURNED'],
    // Exclude LC-only statuses from Scheduler view
    excludeStatusesOnAll: ['HOLD', 'PENDING_CUSTOMER', 'PENDING'],
    isDeferringRefetch: dragIsDeferringRef,
    additionalFields: fieldSelection.additionalFields,
  });

  // Achievement state for "You've Got Pros" easter egg
  const [showAchievementNotification, setShowAchievementNotification] = useState(false);


  const handleNewProsEasterEgg = useCallback(() => {
    const userEmail = currentUser?.username || '';
    if (!userEmail) return;
    api.post('/achievements/record', { achievement_id: 'youve_got_pros', email: userEmail })
      .then(response => {
        if (response.data.first_discovery) {
          setShowAchievementNotification(true);
          celebrateAchievement();
        }
      })
      .catch(err => console.error('Failed to record achievement:', err));
  }, [currentUser?.username]);

  // Show toast when returning to page with new PROs
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && newPronumbers.size > 0) {
        showToast({
          type: 'info',
          message: `You have ${newPronumbers.size} new Pro${newPronumbers.size === 1 ? '' : 's'} to Schedule`,
          duration: 0,
          onDismiss: clearNewPronumbers,
          onEasterEgg: handleNewProsEasterEgg,
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [newPronumbers, showToast, clearNewPronumbers, handleNewProsEasterEgg]);

  // Ref to hold current selection state - allows cells to read current value without causing column recreation
  const selectedLegsRef = useRef(selectedLegs);
  selectedLegsRef.current = selectedLegs;

  // Memoized selection size - only changes when count changes, not on every toggle
  const selectedLegsSize = selectedLegs.size;

  // Terminal options from filter options, labeled with order counts from the
  // loaded grid data. Counts freeze while a region filter is active — the
  // fetch is region-filtered server-side, so other regions vanish from legs.
  const regionCountsRef = useRef<Map<string, number>>(new Map());
  const terminalOptions: MultiSelectOption[] = useMemo(() => {
    if (!filters.terminal?.length) {
      regionCountsRef.current = countOrdersByRegion(legs);
    }
    return buildRegionOptions(filterOptions?.terminals || [], regionCountsRef.current);
  }, [filterOptions, legs, filters.terminal]);

  // Customer options derived from actual legs on the table (use customercode as value, custname as label)
  const customerOptions: MultiSelectOption[] = useMemo(() => {
    const unique = new Map<string, string>();
    legs.forEach((leg) => {
      if (leg.customercode && leg.customer && !unique.has(String(leg.customercode))) {
        unique.set(String(leg.customercode), leg.customer);
      }
    });
    return Array.from(unique.entries()).sort((a, b) => a[1].localeCompare(b[1])).map(([code, name]) => ({ value: code, label: name }));
  }, [legs]);

  // Logistic Coordinator options from filter options (full list so labels persist across filter changes)
  const lcOptions: MultiSelectOption[] = useMemo(() => {
    return filterOptions?.logistics_coordinators.map((lc) => ({ value: lc.value, label: lc.label })) || [];
  }, [filterOptions]);

  // Auto-select the logged-in user in the LC filter on initial load (matches the
  // LC scheduling grid). Match on the full-name label via matchLcToCurrentUser —
  // the option value is the V1 csr short username, not the email local-part.
  const hasAutoSelectedLC = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedLC.current || !lcOptions.length) return;
    const match = matchLcToCurrentUser(lcOptions, currentUser);
    if (match) {
      hasAutoSelectedLC.current = true;
      setFilters({ ...filters, logistic_coordinator: [match.value] });
    }
  }, [lcOptions, currentUser]);

  // Pickup location options from filter options
  const pickupLocationOptions: MultiSelectOption[] = useMemo(() => {
    return filterOptions?.pickup_locations.map((loc) => ({ value: loc, label: loc })) || [];
  }, [filterOptions]);

  // Delivery location options from filter options
  const deliveryLocationOptions: MultiSelectOption[] = useMemo(() => {
    return filterOptions?.delivery_locations.map((loc) => ({ value: loc, label: loc })) || [];
  }, [filterOptions]);

  // Group legs by pronumber for display (one row per order, expandable to show all legs)
  const groupedLegs = useMemo(() => {
    const groups = new Map<number, SchedulingLeg[]>();

    // Group legs by pronumber
    legs.forEach((leg) => {
      const existing = groups.get(leg.pronumber) || [];
      existing.push(leg);
      groups.set(leg.pronumber, existing);
    });

    // Convert to order-level rows with all legs stored for expanded content
    const result: SchedulingLeg[] = [];
    groups.forEach((groupLegs) => {
      // Sort legs by leg_num
      groupLegs.sort((a, b) => a.leg_num - b.leg_num);

      const firstLeg = groupLegs[0];
      const lastLeg = groupLegs[groupLegs.length - 1];

      // Create order-level row with all legs attached
      result.push({
        ...firstLeg,
        pickup_name: firstLeg.orig_name,
        delivery_name: lastLeg.dest_name,
        allLegs: groupLegs, // Store all legs for expanded content
      });
    });

    return result;
  }, [legs]);

  const noteDrafts = useRef(new Map<number, SchedulingNoteDraft>());
  const [copyNotePrompt, setCopyNotePrompt] = useState<{ note: string; resolve: (copy: boolean) => void } | null>(null);
  const copyNotePromptRef = useRef(copyNotePrompt);
  copyNotePromptRef.current = copyNotePrompt;
  useEffect(() => () => copyNotePromptRef.current?.resolve(false), []);

  // Drag-to-fill for date and note columns
  const getRowLocked = useCallback((rowIndex: number, field: string) => {
    const row = groupedLegs[rowIndex];
    if (!row) return true;
    if (row.prepull_needed) return field !== 'proposed_prepull_date' || !isSchedulerLegSelectable(row);
    // Lock by status (applies to all fields)
    if (row.schedule_status !== null && !['PLANNING', 'PENDING_APPT', 'RETURNED'].includes(row.schedule_status)) return true;
    // Prepull column is locked if finalized or sourced from CMS
    if (field === 'proposed_prepull_date' && (row.prepull_finalized || row.prepull_from_cms)) return true;
    return false;
  }, [groupedLegs]);

  const handleDragFillUpdate = useCallback((rowIndex: number, field: string, value: string) => {
    const row = groupedLegs[rowIndex];
    if (!row) return;
    // Auto-select the row when drag-filling a date
    selectLegs([String(row.pronumber)]);
    // Return the promise so drag-to-fill can await all API calls before refetching
    return updateLegSchedule(row.pronumber, { [field]: value });
  }, [groupedLegs, updateLegSchedule, selectLegs]);

  const fillSchedulingRows = useCallback(async (indices: number[], field: string, value: string, sourceIndex: number) => {
    // Snapshot PRO identities before awaiting the user's decision.
    const targets = indices.map(index => groupedLegs[index]).filter(Boolean);
    const source = groupedLegs[sourceIndex];
    const update = await schedulingFillUpdate(field, value, source && noteDrafts.current.get(source.pronumber)?.value, async note => {
      const copy = await new Promise<boolean>(resolve => setCopyNotePrompt({ note, resolve }));
      setCopyNotePrompt(null);
      return copy;
    });
    const results = await Promise.allSettled(targets.map(async row => {
      selectLegs([String(row.pronumber)]);
      await updateLegSchedule(row.pronumber, update);
    }));
    const failed = results.filter(result => result.status === 'rejected').length;
    if (failed) showToast({ type: 'error', message: `Could not update ${failed} PRO${failed === 1 ? '' : 's'}. Check those PROs before retrying.` });
  }, [groupedLegs, selectLegs, updateLegSchedule, showToast]);

  // Auto-select a row when a date is entered
  const handleAutoSelect = useCallback((pronumber: number) => {
    selectLegs([String(pronumber)]);
  }, [selectLegs]);

  // Handle drag end: unset deferral flag and flush any pending refetch
  const handleDragEnd = useCallback(() => {
    dragIsDeferringRef.current = false;
    refetch();
  }, [refetch]);

  const { startDrag: startDateDragRaw } = useDateDragToFill({
    getRowLocked,
    updateRow: handleDragFillUpdate,
    fillRows: fillSchedulingRows,
    onDragEnd: handleDragEnd,
  });

  // Wrap startDrag to set the deferral flag before the drag begins
  const startDateDrag = useCallback((rowIndex: number, field: string, value: string) => {
    dragIsDeferringRef.current = true;
    startDateDragRaw(rowIndex, field, value);
  }, [startDateDragRaw]);

  // Count active advanced filters
  const activeAdvancedFilterCount = useMemo(() => {
    let count = 0;
    if (filters.pickup_from) count++;
    if (filters.deliver_to) count++;
    if (filters.restriction) count++;
    if (filters.prepull_filter) count++;
    if (filters.mileage_band !== undefined) count++;
    return count;
  }, [filters.pickup_from, filters.deliver_to, filters.restriction, filters.prepull_filter, filters.mileage_band]);

  // Dynamic table title based on filters
  const tableTitle = useMemo(() => {
    const parts: string[] = [];

    // Move type (always present)
    if (filters.move_type === 'ALL') {
      parts.push('All Move Types');
    } else {
      parts.push(filters.move_type.charAt(0) + filters.move_type.slice(1).toLowerCase());
    }

    // Delivery type
    if (filters.delivery_type) {
      parts.push(filters.delivery_type);
    }

    // Terminal
    if (filters.terminal && filters.terminal.length > 0) {
      parts.push(filters.terminal.join(', '));
    }

    // Customers (show count if multiple, resolve code to name)
    if (filters.customer && filters.customer.length > 0) {
      if (filters.customer.length === 1) {
        const match = customerOptions.find(o => o.value === filters.customer![0]);
        parts.push(match ? match.label : filters.customer[0]);
      } else {
        parts.push(`${filters.customer.length} Customers`);
      }
    }

    // Advanced filters
    if (activeAdvancedFilterCount > 0) {
      const advParts: string[] = [];
      if (filters.pickup_from) advParts.push(filters.pickup_from);
      if (filters.deliver_to) advParts.push(filters.deliver_to);
      if (filters.restriction) advParts.push(filters.restriction);
      if (filters.prepull_filter) advParts.push(PREPULL_FILTER_OPTIONS.find(o => o.value === filters.prepull_filter)?.label || filters.prepull_filter);
      if (filters.mileage_band !== undefined) advParts.push(`Band ${mileageBandLabel(filters.mileage_band)}`);
      parts.push(`(${advParts.join(', ')})`);
    }

    return `Scheduling: ${parts.join(' - ')}`;
  }, [filters.move_type, filters.delivery_type, filters.terminal, filters.customer, filters.pickup_from, filters.deliver_to, filters.restriction, filters.prepull_filter, filters.mileage_band, activeAdvancedFilterCount, customerOptions]);

  // Handle finalize - re-fetches fresh leg data from CMS then opens modal
  const handleFinalize = useCallback(async () => {
    const selectedKeys = Array.from(selectedLegsRef.current);

    if (selectedKeys.length === 0) {
      showToast({
        type: 'warning',
        message: 'No legs selected to finalize',
      });
      return;
    }

    // Get the local leg objects as a starting point
    const selectedLegObjects = legs.filter((leg) => {
      const key = String(leg.pronumber);
      return selectedLegsRef.current.has(key);
    });

    // Re-fetch fresh data for each selected PRO to get current has_prepull_leg from CMS
    try {
      const freshResults = await Promise.all(
        selectedLegObjects.map((leg) => schedulingService.getOrderLegs(leg.pronumber))
      );
      const freshByPro = new Map<number, SchedulingLeg>();
      freshResults.flat().forEach((leg) => freshByPro.set(leg.pronumber, leg));

      const mergedLegs = selectedLegObjects.map((leg) => {
        const fresh = freshByPro.get(leg.pronumber);
        if (fresh) {
          // is_missing_weight / is_missing_haz_docs ride along so a weight or a
          // haz doc entered a moment ago lifts the block without a grid refresh.
          return { ...leg, has_prepull_leg: fresh.has_prepull_leg, proposed_prepull_date: fresh.proposed_prepull_date, proposed_appt_date: leg.confirmed_delivery_date || fresh.proposed_appt_date, is_missing_weight: fresh.is_missing_weight, is_missing_haz_docs: fresh.is_missing_haz_docs, is_hazmat: fresh.is_hazmat ?? leg.is_hazmat, move_type: fresh.move_type ?? leg.move_type };
        }
        return leg;
      });

      setLegsToFinalize(mergedLegs);
    } catch {
      setLegsToFinalize(selectedLegObjects);
    }

    setShowFinalizeModal(true);
  }, [legs, showToast]);

  // Confirm finalize - called when modal confirms
  const confirmFinalize = useCallback(async () => {
    const prosData = legsToFinalize.map((leg) => ({
      pronumber: leg.pronumber,
    }));

    setFinalizing(true);
    try {
      const { count, errors } = await finalizeLegs(prosData);
      if (errors && errors.length > 0) {
        showToast({
          type: count > 0 ? 'warning' : 'error',
          message: count > 0
            ? `Finalized ${count} leg${count !== 1 ? 's' : ''}, but ${errors.length} failed: ${errors.join('; ')}`
            : `Failed to finalize: ${errors.join('; ')}`,
        });
      } else {
        showToast({
          type: 'success',
          message: `Finalized ${count} leg${count !== 1 ? 's' : ''}`,
        });
      }
      setShowFinalizeModal(false);
      setLegsToFinalize([]);
    } catch (err: any) {
      showToast({
        type: 'error',
        message: err.message || 'Failed to finalize legs',
      });
    } finally {
      setFinalizing(false);
    }
  }, [legsToFinalize, finalizeLegs, showToast]);

  // Set footer buttons with active users display
  useEffect(() => {
    // Filter active users to exclude current user and duplicates
    // Identify ourselves by account id, not by socket id: presence is keyed per
    // user server-side, so with the page open in two tabs only the newest tab's
    // sid appears in the list and the older one would fail to find itself.
    const currentUserId = currentUser?.localAccountId;
    const seen = new Set<string>();
    const otherUsers = activeUsers.filter(user => {
      if (user.user_id === currentUserId || seen.has(user.user_id)) {
        return false;
      }
      seen.add(user.user_id);
      return true;
    });
    const maxVisible = 10;
    const visibleUsers = otherUsers.slice(0, maxVisible);
    const overflowUsers = otherUsers.slice(maxVisible);

    const getInitials = (name: string): string => {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    const getUserColor = (name: string): string => {
      const colors = [
        'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500',
        'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
      ];
      const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    };

    setDispatchButton(
      <div className="flex items-center justify-between w-full">
        {/* Left side - Active users and clear selection */}
        <div className="flex items-center gap-2">
          {otherUsers.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <UsersIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
              <div className="flex -space-x-1">
                {visibleUsers.map(user => (
                  <Tooltip key={user.user_id} content={user.user_name}>
                    <div
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white dark:border-gray-700 text-[0.625rem] font-medium text-white cursor-pointer ${getUserColor(user.user_name)}`}
                    >
                      {getInitials(user.user_name)}
                    </div>
                  </Tooltip>
                ))}
                {overflowUsers.length > 0 && (
                  <Tooltip
                    content={
                      <div className="flex flex-col gap-1">
                        {overflowUsers.map(user => (
                          <span key={user.user_id}>{user.user_name}</span>
                        ))}
                      </div>
                    }
                  >
                    <div className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white dark:border-gray-700 bg-gray-500 text-[0.625rem] font-medium text-white cursor-pointer">
                      +{overflowUsers.length}
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          )}
          {activeTab === 'scheduler' && selectedLegs.size > 0 && (
            <button
              onClick={() => clearSelection()}
              className="px-4 py-1.5 text-sm font-medium text-muted hover:text-gray-700 dark:hover:text-gray-200"
            >
              Clear Selection
            </button>
          )}
        </div>
        {/* Right side - Action buttons */}
        {activeTab === 'scheduler' && <div className="flex gap-2">
          <button
            onClick={() => {
              // Capture the selected legs before opening modal
              const selectedLegObjects = legs.filter((leg) =>
                selectedLegsRef.current.has(String(leg.pronumber))
              );

              // Validate: PROs with prepull from CMS must have an appt date
              const invalidLegs = selectedLegObjects.filter(
                (leg) => leg.prepull_from_cms && !leg.proposed_appt_date
              );

              if (invalidLegs.length > 0) {
                const proNumbers = invalidLegs.map((l) => l.pronumber).join(', ');
                showToast({
                  type: 'error',
                  message: `PRO${invalidLegs.length > 1 ? 's' : ''} ${proNumbers} ha${invalidLegs.length > 1 ? 've' : 's'} a prepull date from CMS. Please add an appointment date before sending to LC.`,
                });
                return;
              }

              setLegsToSendToLC(selectedLegObjects);
              setShowSendToLCModal(true);
            }}
            disabled={selectedLegs.size === 0 || legs.some(leg => selectedLegs.has(String(leg.pronumber)) && leg.prepull_needed && missingSchedulingDate(leg))}
            title={legs.some(leg => selectedLegs.has(String(leg.pronumber)) && leg.prepull_needed && missingSchedulingDate(leg)) ? 'Enter a pre-pull date before sending to LC' : undefined}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            Send to LC ({selectedLegs.size})
          </button>
          <button
            onClick={handleFinalize}
            disabled={selectedLegs.size === 0}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckIcon className="w-4 h-4" />
            Finalize ({selectedLegs.size})
          </button>
        </div>}
      </div>
    );

    return () => setDispatchButton(null);
  }, [activeTab, selectedLegs, setDispatchButton, clearSelection, handleFinalize, legs, showToast, activeUsers, currentUser?.localAccountId]);

  // Set up icon legend sections
  useEffect(() => {
    setLegendSections(schedulerLegendSections, 'Scheduler Icon Legend');
  }, [setLegendSections]);

  // Handle send to LC
  const handleSendToLC = useCallback(async () => {
    const prosToSend = legsToSendToLC.map((leg) => ({
      pronumber: leg.pronumber,
    }));

    setSendingToLC(true);
    try {
      const count = await sendToLC(prosToSend);
      showToast({
        type: 'success',
        message: `Sent ${count} leg${count !== 1 ? 's' : ''} to LC`,
      });
      setShowSendToLCModal(false);
      setLegsToSendToLC([]);
    } catch (err: any) {
      showToast({
        type: 'error',
        message: err.message || 'Failed to send to LC',
      });
    } finally {
      setSendingToLC(false);
    }
  }, [legsToSendToLC, sendToLC, showToast]);

  // Open order details drawer
  const openOrderModal = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsModalOpen(true);
  }, []);

  // Close order details drawer
  const closeOrderModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPro(null);
  }, []);

  // Handle row click to open drawer
  const handleRowClick = useCallback((leg: SchedulingLeg) => {
    openOrderModal(leg.pronumber);
  }, [openOrderModal]);

  // Equipment icons renderer
  const renderEquipmentIcons = useCallback((leg: SchedulingLeg) => {
    const icons = [];
    if (leg.is_hot) {
      icons.push(
        <Tooltip key="hot" content="HOT Load" placement="top">
          <span className="gw-icon gw-icon-hotjar text-red-500 cursor-help" style={{ fontSize: '16px' }} />
        </Tooltip>
      );
    }
    if (leg.is_hazmat) {
      icons.push(
        <Tooltip key="hazmat" content="Hazmat" placement="top">
          <span className="flaticon flaticon-warning text-orange-500 cursor-help" style={{ fontSize: '16px' }} />
        </Tooltip>
      );
    }
    if (leg.is_overweight || leg.overweight_approval_pending) {
      icons.push(
        <Tooltip key="overweight" content={leg.overweight_approval_pending ? "Overweight permit approval required; LC cannot finalize" : "Overweight"} placement="top">
          <span className={`flaticon flaticon-weight ${leg.overweight_approval_pending ? 'text-red-600' : 'text-heading'} cursor-help`} style={{ fontSize: '16px' }} />
        </Tooltip>
      );
    }
    if (leg.is_early) {
      icons.push(
        <Tooltip key="early" content="Early Appointment" placement="top">
          <span className="gw-icon gw-icon-early text-heading cursor-help" style={{ fontSize: '16px' }} />
        </Tooltip>
      );
    }
    if (leg.is_triaxle) {
      icons.push(
        <Tooltip key="triaxle" content="Triaxle" placement="top">
          <span className="gw-icon gw-icon-dual-axle text-blue-600 cursor-help" style={{ fontSize: '16px' }} />
        </Tooltip>
      );
    }
    if (leg.is_93k) {
      icons.push(
        <Tooltip key="93k" content="93K Chassis" placement="top">
          <span className="gw-icon text-heading cursor-help" style={{ fontSize: '16px' }}>&#xE9E5;</span>
        </Tooltip>
      );
    }
    if (leg.is_swing) {
      icons.push(
        <Tooltip key="swing" content="Swing" placement="top">
          <span className="flaticon flaticon-cargo text-purple-600 cursor-help" style={{ fontSize: '16px' }} />
        </Tooltip>
      );
    }
    if (leg.is_tank) {
      icons.push(
        <Tooltip key="tank" content="Tank" placement="top">
          <span className="flaticon flaticon-oil-tank text-purple-600 cursor-help" style={{ fontSize: '16px' }} />
        </Tooltip>
      );
    }
    if (leg.is_daycab) {
      icons.push(
        <Tooltip key="daycab" content="Daycab" placement="top">
          <span className="gw-icon gw-icon-daycab text-blue-700 cursor-help" style={{ fontSize: '16px' }} />
        </Tooltip>
      );
    }
    if (leg.is_reefer) {
      icons.push(
        <Tooltip key="reefer" content="Reefer" placement="top">
          <span className="cursor-help">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" className="text-blue-500 fill-current">
              <path d="M11,24c-6.079.117-9.334-7.638-5-11.89V5c.211-6.609,9.791-6.6,10,0v7.11C20.335,16.363,17.077,24.117,11,24ZM11,2A3,3,0,0,0,8,5v7.537a1,1,0,0,1-.332.744A5.018,5.018,0,0,0,11,22a5.018,5.018,0,0,0,3.332-8.719A1,1,0,0,1,14,12.537V5A3,3,0,0,0,11,2Zm0,18a3.007,3.007,0,0,1-1-5.829V12a1,1,0,0,1,2,0v2.171A3.007,3.007,0,0,1,11,20Zm0-4a1,1,0,0,0,0,2A1,1,0,0,0,11,16ZM21,6a3,3,0,0,1,0-6A3,3,0,0,1,21,6Zm0-4a1,1,0,0,0,0,2A1,1,0,0,0,21,2Z"/>
            </svg>
          </span>
        </Tooltip>
      );
    }
    if (leg.is_ppe_required) {
      icons.push(
        <Tooltip key="ppe" content="PPE Required" placement="top">
          <ShieldCheckIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400 cursor-help" />
        </Tooltip>
      );
    }
    return <div className="flex gap-1">{icons}</div>;
  }, []);

  // Flag icons renderer (Reschedule Required, Missing Weight)
  const renderFlagIcons = useCallback((leg: SchedulingLeg) => {
    const flags = [];
    if (leg.is_reschedule_required) {
      const rescheduleHistory = leg.reschedule_history || [];
      const tooltipContent = rescheduleHistory.length > 0 ? (
        <div className="text-xs space-y-2 max-w-xs">
          <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-gray-900">
            Service Failure History ({leg.reschedule_count})
          </div>
          {rescheduleHistory.map((entry, idx) => (
            <div key={idx} className="space-y-0.5">
              <div className="text-gray-500">{entry.old_date} → {entry.new_date}</div>
              <div className="text-gray-900 font-medium">{entry.reason || 'No reason provided'}</div>
              <div className="text-gray-400 text-[0.625rem]">{entry.created}</div>
            </div>
          ))}
        </div>
      ) : `${leg.reschedule_count} service failure${leg.reschedule_count !== 1 ? 's' : ''}`;

      flags.push(
        <Tooltip key="reschedule" content={tooltipContent} placement="right">
          <span
            className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded cursor-help ${
              leg.reschedule_count >= 2 ? 'bg-red-500' : 'bg-orange-500'
            }`}
          >
            {leg.reschedule_count}
          </span>
        </Tooltip>
      );
    }
    // Backend sets is_missing_weight only for IMPORT PROs with lbs NULL/0.
    // Text pill (like COD) rather than an icon: Finalize is blocked on it.
    if (leg.is_missing_weight) {
      flags.push(
        <Tooltip key="missing-weight" content={SCHEDULING_MISSING_WEIGHT_TOOLTIP} placement="top">
          <span className="inline-flex items-center justify-center px-1 h-5 text-[0.625rem] font-bold text-white rounded bg-red-600 cursor-help">
            NO WT
          </span>
        </Tooltip>
      );
    }
    // Backend sets is_missing_haz_docs only for Haz=Y PROs with no HAZ-MAT PAPERWORK doc.
    if (legBlockedForMissingHazDocs(leg)) {
      flags.push(
        <Tooltip key="missing-haz-docs" content={SCHEDULING_MISSING_HAZ_DOCS_TOOLTIP} placement="top">
          <span className="inline-flex items-center justify-center px-1 h-5 text-[0.625rem] font-bold text-white rounded bg-red-600 cursor-help">
            NO HAZ DOCS
          </span>
        </Tooltip>
      );
    }
    if (leg.is_cod) {
      flags.push(
        <Tooltip key="cod" content="COD — Cash on Delivery" placement="top">
          <span className="inline-flex items-center justify-center px-1 h-5 text-[0.625rem] font-bold text-white rounded bg-green-600 cursor-help">
            COD
          </span>
        </Tooltip>
      );
    }
    if (leg.inbond_type?.trim()) {
      flags.push(
        <Tooltip key="bonded" content={`Bonded Cargo: ${leg.inbond_type}`} placement="top">
          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded bg-blue-600 cursor-help">
            B
          </span>
        </Tooltip>
      );
    }
    // Check if rail order is missing rail pickup number
    const isRailOrder = ((leg.mode === 'MLB' && leg.move_type === 'IMPORT') || leg.move_type === 'RAIL TO PORT') && (leg.orig_location_type === 'RAIL' || leg.orig_require_pickup === 'Y');
    if (isRailOrder && !leg.rail_pickup_number?.trim()) {
      flags.push(
        <Tooltip key="missing-rail-pickup" content="Missing Rail Pickup #" placement="top">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 text-red-500 fill-current cursor-help">
            <path d="m24,21l-.294-3h-2.336l-.196-2h2.336l-.294-3h-2.336l-.196-2h2.336l-.294-3h-2.336l-.196-2h2.336l-.294-3h-2.336l-.294-3h-3.014l.294,3H7.115l.294-3h-3.014l-.294,3H1.765l-.294,3h2.336l-.196,2H1.274l-.294,3h2.336l-.196,2H.784l-.294,3h2.336l-.196,2H.294l-.294,3h2.336l-.294,3h3.014l.294-3h13.3l.294,3h3.014l-.294-3h2.336ZM6.821,6h10.359l.196,2H6.625l.196-2Zm-.49,5h11.339l.196,2H6.134l.196-2Zm-.686,7l.196-2h12.319l.196,2H5.644Z"/>
          </svg>
        </Tooltip>
      );
    }
    // Show comment icon if scheduling comments exist
    if (leg.scheduling_comments?.trim()) {
      flags.push(
        <Tooltip key="comment" content={leg.scheduling_comments} placement="top">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 text-blue-500 cursor-help">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
          </svg>
        </Tooltip>
      );
    }
    return flags.length > 0 ? <div className="flex gap-1 items-center">{flags}</div> : null;
  }, []);

  // Render expanded content panel for an order (matching LC layout with history sidebar)
  const renderExpandedContent = useCallback((row: SchedulingLeg) => {
    // Fetch history when expanded (if not already loaded)
    if (!historyData[row.pronumber] && !historyLoading.has(row.pronumber)) {
      setHistoryLoading(prev => new Set(prev).add(row.pronumber));
      schedulingService.getHistory(row.pronumber).then(data => {
        setHistoryData(prev => ({ ...prev, [row.pronumber]: data }));
      }).catch(() => {
        showToast({ type: 'error', message: 'Failed to load history' });
      }).finally(() => {
        setHistoryLoading(prev => {
          const next = new Set(prev);
          next.delete(row.pronumber);
          return next;
        });
      });
    }

    return (
      <SchedulerExpandedPanel
        row={row}
        historyRecords={historyData[row.pronumber] || []}
        historyLoading={historyLoading.has(row.pronumber)}
        onUpdateSchedule={updateLegSchedule}
      />
    );
  }, [updateLegSchedule, historyData, historyLoading, showToast]);

  // Table columns
  const baseColumns: ColumnDef<SchedulingLeg>[] = useMemo(
    () => [
      {
        accessorKey: 'pronumber',
        header: 'Pro #',
        size: 100,
        minSize: 60,
        cell: ({ row }) => {
          const pronumber = row.original.pronumber;
          // Viewed/last-viewed highlight + click-to-mark is applied by TanTable's
          // trackViewedColumnId="pronumber" wrapper.
          return (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openOrderModal(pronumber);
                }}
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
              >
                {pronumber}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openHistoryDrawer(pronumber);
                }}
                className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title="Scheduling History"
                aria-label={`Scheduling history for pro ${pronumber}`}
              >
                <ClockIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        },
      },
      {
        id: 'select',
        header: ({ table }) => {
          const pageRows = table.getRowModel().rows;
          const selectableRows = pageRows.filter(r => {
            return isSchedulerLegSelectable(r.original);
          });
          const allSelected = selectableRows.length > 0 && selectableRows.every(r => selectedLegsRef.current.has(String(r.original.pronumber)));
          return (
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => {
                const keys = selectableRows.map(r => String(r.original.pronumber));
                if (allSelected) {
                  deselectLegs(keys);
                } else {
                  selectLegs(keys);
                }
              }}
              className="h-4 w-4 rounded border-gray-300"
            />
          );
        },
        cell: ({ row }) => {
          // Read from ref to get current value, selectedLegsSize in deps triggers re-render
          const currentSelection = selectedLegsRef.current;
          const isLocked = !isSchedulerLegSelectable(row.original);
          const key = String(row.original.pronumber);
          return (
            <input
              type="checkbox"
              checked={currentSelection.has(key)}
              onChange={(e) => {
                e.stopPropagation();
                toggleLegSelection(row.original.pronumber);
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-gray-300"
              disabled={isLocked}
            />
          );
        },
        size: 40,
        minSize: 60,
      },
      {
        accessorKey: 'customer',
        header: 'Customer',
        size: 120,
      },
      {
        accessorKey: 'logistics_coordinator',
        header: 'LC',
        size: 100,
      },
      {
        accessorKey: 'move_type',
        header: 'Type',
        size: 80,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return value || <span className="text-gray-400">-</span>;
        },
      },
      {
        accessorKey: 'has_prepull_leg',
        header: 'Prepull',
        size: 50,
        cell: ({ getValue }) => getValue() ? 'Y' : '',
      },
      {
        id: 'is_live',
        accessorFn: (row) => !row.is_drop,
        header: 'Live',
        size: 50,
        cell: ({ getValue }) => {
          const isLive = getValue() as boolean;
          return isLive ? '✓' : '';
        },
      },
      {
        accessorKey: 'key_account',
        header: 'Key',
        size: 50,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          if (!value || value.trim() === '') return <span className="text-gray-400">-</span>;

          const upper = value.toUpperCase();
          let display = '';
          let colorClass = 'text-gray-400';

          // Average - don't display
          if (upper === 'AVERAGE') {
            return <span className="text-gray-400">-</span>;
          }
          // Contract -> CONT (limegreen)
          else if (upper === 'CONTRACT') {
            display = 'CONT';
            colorClass = 'text-lime-500 font-medium';
          }
          // Key Accounts 1-4 -> just the number with colors
          else if (upper.startsWith('KEY ACCOUNTS ') || upper.startsWith('KEY ACCOUNT ')) {
            const num = upper.startsWith('KEY ACCOUNTS ') ? value.substring(13) : value.substring(12);
            display = num;
            if (num === '1') colorClass = 'text-red-600 font-medium';
            else if (num === '2') colorClass = 'text-orange-500 font-medium';
            else if (num === '3') colorClass = 'text-yellow-500 font-medium';
            else if (num === '4') colorClass = 'text-green-600 font-medium';
          }
          // Key Prospect 1-4 -> KP X (purple)
          else if (upper.startsWith('KEY PROSPECT ')) {
            display = 'KP ' + value.substring(13);
            colorClass = 'text-purple-600 font-medium';
          }
          else {
            display = value;
          }

          return <span className={colorClass}>{display}</span>;
        },
      },
      {
        id: 'equipment',
        header: 'Req',
        cell: ({ row }) => renderEquipmentIcons(row.original),
        size: 120,
      },
      {
        id: 'flags',
        header: 'Flags',
        cell: ({ row }) => renderFlagIcons(row.original),
        size: 60,
      },
      {
        accessorKey: 'trouble_ticket_count',
        header: 'TT',
        size: 50,
        cell: ({ row }) => (
          <TroubleTicketBadge
            tickets={row.original.trouble_tickets || []}
            count={row.original.trouble_ticket_count}
            onClick={() => openOrderModal(row.original.pronumber)}
          />
        ),
      },
      {
        id: 'pickup_origin',
        header: 'Pickup',
        size: 150,
        accessorFn: (row: SchedulingLeg) => row.pickup_name || row.orig_name || '',
        meta: { exportValue: (row: any) => row.pickup_name || row.orig_name || '' },
        cell: ({ row }) => {
          const name = row.original.pickup_name || row.original.orig_name;
          const hours = row.original.pickup_hours;
          const maxLane = row.original.pickup_max_lane_capacity;
          const maxDel = row.original.pickup_max_deliveries;
          const hasInfo = hours || maxLane != null || maxDel != null;
          return hasInfo ? (
            <Tooltip content={<div className="text-xs">{hours && <div>Hours: {hours}</div>}{maxLane != null && <div>Max Lane Capacity: {maxLane}</div>}{maxDel != null && <div>Max Deliveries: {maxDel}</div>}</div>} placement="top">
              <span>{name}</span>
            </Tooltip>
          ) : name;
        },
      },
      {
        id: 'delivery_dest',
        header: 'Delivery',
        size: 150,
        accessorFn: (row: SchedulingLeg) => row.delivery_name || row.dest_name || '',
        meta: { exportValue: (row: any) => row.delivery_name || row.dest_name || '' },
        cell: ({ row }) => {
          const name = row.original.delivery_name || row.original.dest_name;
          const hours = row.original.delivery_hours;
          const maxLane = row.original.delivery_max_lane_capacity;
          const maxDel = row.original.delivery_max_deliveries;
          const hasInfo = hours || maxLane != null || maxDel != null;
          return hasInfo ? (
            <Tooltip content={<div className="text-xs">{hours && <div>Hours: {hours}</div>}{maxLane != null && <div>Max Lane Capacity: {maxLane}</div>}{maxDel != null && <div>Max Deliveries: {maxDel}</div>}</div>} placement="top">
              <span>{name}</span>
            </Tooltip>
          ) : name;
        },
      },
      {
        accessorKey: 'rail_pickup_number',
        header: 'Rail Pickup #',
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return value || <span className="text-gray-400">-</span>;
        },
      },
      {
        accessorKey: 'mileage_band',
        header: 'Mileage Band',
        size: 140,
        cell: ({ row }) => mileageBandLabel(row.original.mileage_band),
      },
      {
        accessorKey: 'miles',
        header: 'Miles',
        size: 70,
        cell: ({ getValue }) => {
          const miles = getValue() as number | null;
          return miles != null ? miles.toLocaleString() : '-';
        },
      },
      {
        accessorKey: 'last_free_day',
        header: 'LFD',
        size: 100,
        cell: ({ row }) => {
          const lfd = row.original.last_free_day;
          const prepull = row.original.proposed_prepull_date;
          const status = row.original.schedule_status;
          const colorClass = getLfdColorClass(lfd, prepull, status);
          return <span className={colorClass}>{formatDate(lfd)}</span>;
        },
      },
      {
        accessorKey: 'cutoff_date',
        header: 'Cutoff',
        size: 100,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          const colorClass = getCutoffPerDiemColorClass(value);
          return <span className={colorClass}>{formatDate(value)}</span>;
        },
      },
      {
        accessorKey: 'per_diem_date',
        header: 'Per Diem',
        size: 100,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          const colorClass = getCutoffPerDiemColorClass(value);
          return <span className={colorClass}>{formatDate(value)}</span>;
        },
      },
      {
        accessorKey: 'first_rcv_date',
        header: 'First RCV',
        size: 100,
        cell: ({ getValue }) => formatDate(getValue() as string | null),
      },
      {
        accessorKey: 'storage_cleared_date',
        header: 'Storage Cleared',
        size: 115,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return <span>{formatDate(value)}</span>;
        },
      },
      {
        accessorKey: 'target_on_or_by',
        header: 'On|By',
        size: 55,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return <span>{value || '-'}</span>;
        },
      },
      {
        accessorKey: 'target_delivery_date',
        header: 'Target Deliv',
        size: 105,
        cell: ({ row }) => {
          const value = row.original.target_delivery_date;
          const sameDay = isSamePrepullAndDelivery(row.original.proposed_prepull_date, value);
          return <span className={sameDay ? 'bg-yellow-200 rounded px-1' : ''}>{formatDate(value)}</span>;
        },
      },
      {
        accessorKey: 'customer_target_date',
        header: 'Cust Target',
        size: 105,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return <span>{formatDate(value)}</span>;
        },
      },
      {
        accessorKey: 'proposed_prepull_date',
        header: 'PrePull',
        size: 115,
        minSize: 120,
        cell: ({ row }) => {
          // If prepull came from CMS OR prepull has been finalized, show as read-only text
          const isPrepullLocked = !row.original.prepull_needed && (row.original.prepull_from_cms || row.original.prepull_finalized);
          const sameDay = isSamePrepullAndDelivery(row.original.proposed_prepull_date, row.original.target_delivery_date);
          if (isPrepullLocked) {
            const dateStr = row.original.proposed_prepull_date;
            if (!dateStr) return <span className="text-gray-400">-</span>;
            return <span className={`font-bold${sameDay ? ' bg-yellow-200 rounded px-1' : ''}`}>{new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}</span>;
          }
          // Lock prepull date if status is not PLANNING, PENDING_APPT, or RETURNED, or if prepull has been finalized
          const isLocked = !isSchedulerLegSelectable(row.original);
          const fieldName = 'prepull_date';
          const editor = getFieldEditor(row.original.pronumber, fieldName);
          return (
            <div className={sameDay ? 'bg-yellow-200 rounded' : ''}>
              <DateCell
                dateStr={row.original.proposed_prepull_date}
                isLocked={isLocked}
                pronumber={row.original.pronumber}
                field="proposed_prepull_date"
                onUpdate={updateLegSchedule}
                onAutoSelect={handleAutoSelect}
                editor={editor}
                onFocus={() => broadcastFieldFocus(row.original.pronumber, fieldName)}
                onBlur={() => broadcastFieldBlur(row.original.pronumber, fieldName)}
                rowIndex={row.index}
                onStartDrag={startDateDrag}
              />
              {prepullConflict(row.original, row.original.proposed_prepull_date) && (
                <span className="text-xs text-red-600 dark:text-red-400">
                  {prepullConflict(row.original, row.original.proposed_prepull_date)}
                </span>
              )}
            </div>
          );
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.proposed_prepull_date;
          const b = rowB.original.proposed_prepull_date;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return a.localeCompare(b);
        },
      },
      {
        accessorKey: 'proposed_appt_date',
        header: 'Appt Date',
        size: 115,
        minSize: 120,
        cell: ({ row }) => {
          if (row.original.prepull_needed) {
            return <span title="Confirmed delivery — preserved during pre-pull scheduling" className="font-bold">
              {formatDate(row.original.confirmed_delivery_date)}
            </span>;
          }
          const isLocked = row.original.schedule_status !== null && !['PLANNING', 'PENDING_APPT', 'RETURNED'].includes(row.original.schedule_status);
          const fieldName = 'appt_date';
          const editor = getFieldEditor(row.original.pronumber, fieldName);
          return (
            <DateCell
              dateStr={row.original.proposed_appt_date}
              isLocked={isLocked}
              pronumber={row.original.pronumber}
              field="proposed_appt_date"
              onUpdate={updateLegSchedule}
              onAutoSelect={handleAutoSelect}
              editor={editor}
              onFocus={() => broadcastFieldFocus(row.original.pronumber, fieldName)}
              onBlur={() => broadcastFieldBlur(row.original.pronumber, fieldName)}
              rowIndex={row.index}
              onStartDrag={startDateDrag}
            />
          );
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.proposed_appt_date;
          const b = rowB.original.proposed_appt_date;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return a.localeCompare(b);
        },
      },
      {
        id: 'appt_time',
        header: 'Appt Time',
        size: 145,
        cell: ({ row }) => (
          <TimeCell
            startTime={row.original.appt_time_start}
            endTime={row.original.appt_time_end}
          />
        ),
      },
      {
        id: 'scheduling_note',
        header: 'Notes',
        size: 240,
        minSize: 180,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const pro = row.original.pronumber;
          let draft = noteDrafts.current.get(pro);
          if (!draft) {
            draft = { value: '', saved: '' };
            noteDrafts.current.set(pro, draft);
          }
          return <SchedulingNoteCell
            draft={draft}
            pronumber={pro}
            disabled={getRowLocked(row.index, 'scheduling_note')}
            onSave={note => updateLegSchedule(pro, { scheduling_note: note })}
            onDrag={note => startDateDrag(row.index, 'scheduling_note', note)}
            onError={() => showToast({ type: 'error', message: `Could not save the note for PRO ${pro}. Please retry.` })}
          />;
        },
      },
      {
        accessorKey: 'schedule_status',
        header: 'Status',
        size: 100,
        minSize: 100,
        meta: {
          exportValue: (row: SchedulingLeg) => row.prepull_needed ? 'Pre-pull needed' : SCHEDULE_STATUS_LABELS[row.schedule_status as ScheduleStatus] || row.schedule_status || '-',
        },
        cell: ({ getValue, row }) => {
          if (row.original.prepull_needed) {
            return <div className="flex flex-col gap-1">
              <span className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-900">Pre-pull needed</span>
              <span className="text-xs text-green-700 dark:text-green-400">Delivery confirmed</span>
              {row.original.prepull_holds?.map(hold => <span key={hold} className="text-xs font-semibold text-red-600 dark:text-red-400">{hold}</span>)}
            </div>;
          }
          const status = getValue() as ScheduleStatus | null;
          if (!status) {
            return <span className="text-gray-400">-</span>;
          }
          const isSystemReturn = status === 'RETURNED' && row.original.returned_by === 'SYSTEM';
          return (
            <span className="inline-flex items-center gap-1">
              <span className={`px-2 py-1 text-xs font-medium rounded ${SCHEDULE_STATUS_COLORS[status]}`}>
                {SCHEDULE_STATUS_LABELS[status] || status}
              </span>
              {isSystemReturn && (
                <Tooltip content="Auto-returned by the system — appointment date was removed in V1 CMS after finalization">
                  <span className="cursor-help inline-flex" role="img" aria-label="Auto-returned by system">
                    <RobotIcon className="w-4 h-4 text-muted" />
                  </span>
                </Tooltip>
              )}
            </span>
          );
        },
      },
    ],
    // selectedLegsSize triggers column update only when count changes
    // Individual cell checkboxes read from selectedLegsRef.current for actual selection state
    // DateCell/TimeCell are memoized to prevent flashing during selection changes
    // Note: fieldEditors removed from deps - getFieldEditor uses ref internally for stability
    [selectedLegsSize, toggleLegSelection, selectLegs, deselectLegs, renderEquipmentIcons, renderFlagIcons, updateLegSchedule, handleAutoSelect, openOrderModal, openHistoryDrawer, getFieldEditor, broadcastFieldFocus, broadcastFieldBlur, startDateDrag, getRowLocked, showToast]
  );

  const { columns, availableFields } = useAdditionalFieldColumns<SchedulingLeg>(
    fieldSelection, { baseColumns },
  );

  return (
    <>
    <WarningConfirmModal
      open={copyNotePrompt !== null}
      title="Copy scheduling note?"
      message={<><p>Also copy this note to the PROs receiving the appointment date?</p><p className="mt-2 whitespace-pre-wrap">{copyNotePrompt?.note}</p></>}
      confirmLabel="Yes, copy note"
      cancelLabel="No, date only"
      onConfirm={() => copyNotePrompt?.resolve(true)}
      onCancel={() => copyNotePrompt?.resolve(false)}
    />
    {showAchievementNotification && createPortal(
      <NotificationContainer position="top-center">
        <Notification
          type="success"
          title="Congrats!"
          message={<span>You found the <span className="font-semibold text-blue-900 dark:text-blue-300">You've Got Pros</span> Easter Egg</span>}
          position="top-center"
          showClose={true}
          onClose={() => setShowAchievementNotification(false)}
          autoDismiss={5000}
        />
      </NotificationContainer>,
      document.body
    )}
    <div className="flex flex-col h-full min-h-0">
      {/* Filter Bar */}
      <FilterBar label="Filters:">
        {activeTab === 'scheduler' && <MultiSelect
          options={STATUS_OPTIONS}
          value={filters.status || []}
          onChange={(values) => {
            const prevValues = filters.status || [];
            const hadAll = prevValues.includes('ALL' as ScheduleStatus);
            const hasAll = values.includes('ALL');

            let newStatus: ScheduleStatus[];
            if (hasAll && !hadAll) {
              // User just selected "ALL" - clear others
              newStatus = ['ALL' as ScheduleStatus];
            } else if (hasAll && values.length > 1) {
              // User selected a specific status while "ALL" was selected - remove "ALL"
              newStatus = values.filter(v => v !== 'ALL') as ScheduleStatus[];
            } else if (values.length === 0) {
              // Nothing selected - default to "ALL"
              newStatus = ['ALL' as ScheduleStatus];
            } else {
              newStatus = values as ScheduleStatus[];
            }
            setFilters({ ...filters, status: newStatus });
          }}
          placeholder="All Statuses"
          size="sm"
          className="min-w-[130px]"
        />}
        <MultiSelect
          options={MOVE_TYPE_OPTIONS}
          value={[filters.move_type]}
          onChange={(values) => {
            // Use the last selected value (the newly clicked one) for single-select behavior
            const newValue = values.length > 0 ? values[values.length - 1] : 'ALL';
            setFilters({ ...filters, move_type: newValue as 'ALL' | 'IMPORT' | 'EXPORT' | 'DOMESTIC' });
          }}
          placeholder="Move Type"
          size="sm"
          className="min-w-[120px]"
        />
        <MultiSelect
          options={DELIVERY_TYPE_OPTIONS}
          value={filters.delivery_type ? [filters.delivery_type] : []}
          onChange={(values) => {
            const newValue = values.length > 0 ? values[values.length - 1] : undefined;
            setFilters({ ...filters, delivery_type: newValue });
          }}
          placeholder="All Delivery Types"
          showClearOption
          size="sm"
          className="min-w-[140px]"
        />
        <MultiSelect
          options={terminalOptions}
          value={filters.terminal || []}
          onChange={(values) => setFilters({ ...filters, terminal: values.length > 0 ? values : undefined })}
          placeholder="Regions"
          showClearOption
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={customerOptions}
          value={filters.customer || []}
          onChange={(values) => setFilters({ ...filters, customer: values })}
          placeholder="All Customers"
          enableSearch
          size="sm"
          className="min-w-[140px]"
        />
        <MultiSelect
          options={lcOptions}
          value={filters.logistic_coordinator || []}
          onChange={(values) => setFilters({ ...filters, logistic_coordinator: values.length > 0 ? values : undefined })}
          placeholder="All LCs"
          enableSearch
          size="sm"
          className="min-w-[130px]"
        />
        <button
          type="button"
          onClick={() => setFilters({
            move_type: 'ALL',
            terminal: undefined,
            delivery_type: undefined,
            status: ['ALL'],
            customer: undefined,
            logistic_coordinator: undefined,
            pickup_from: undefined,
            deliver_to: undefined,
            restriction: undefined,
            prepull_filter: undefined,
            pronumber: undefined,
          })}
          className="text-sm text-muted hover:text-gray-700 dark:hover:text-gray-200"
        >
          Clear All Filters
        </button>
        <FilterBar.Spacer />
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search Pro #"
            value={filters.pronumber || ''}
            onChange={(e) => {
              const value = e.target.value;
              setFilters({ ...filters, pronumber: value ? Number(value) : undefined });
            }}
            className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-[280px]"
          />
        </div>
        <button
          type="button"
          onClick={openLegend}
          className="p-1.5 text-muted hover:text-gray-700 dark:hover:text-gray-200"
          title="Icon Legend"
        >
          <InformationCircleIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => openHistoryDrawer(null)}
          className="p-1.5 text-muted hover:text-gray-700 dark:hover:text-gray-200"
          title="Scheduling History"
          aria-label="Scheduling History"
        >
          <ClockIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => setShowTerminalSettingsModal(true)}
          className="p-1.5 text-muted hover:text-gray-700 dark:hover:text-gray-200"
          title="Terminal Settings"
        >
          <Cog6ToothIcon className="w-5 h-5" />
        </button>
      </FilterBar>
      {/* Advanced Filters Bar */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-2 flex items-start justify-between">
          {/* Left side - Advanced filters */}
          <div className={activeTab === 'scheduler' ? 'flex flex-wrap items-center gap-3' : 'hidden'}>
            <span className="text-sm text-muted">Advanced:</span>
            <MultiSelect
              options={pickupLocationOptions}
              value={filters.pickup_from ? [filters.pickup_from] : []}
              onChange={(values) => {
                const newValue = values.length > 0 ? values[values.length - 1] : undefined;
                setFilters({ ...filters, pickup_from: newValue });
              }}
              placeholder="Pickup From"
              enableSearch
              showClearOption
              loading={loadingOptions}
              size="sm"
              className="min-w-[160px]"
            />
            <MultiSelect
              options={deliveryLocationOptions}
              value={filters.deliver_to ? [filters.deliver_to] : []}
              onChange={(values) => {
                const newValue = values.length > 0 ? values[values.length - 1] : undefined;
                setFilters({ ...filters, deliver_to: newValue });
              }}
              placeholder="Deliver To"
              enableSearch
              showClearOption
              loading={loadingOptions}
              size="sm"
              className="min-w-[160px]"
            />
            <MultiSelect
              options={RESTRICTION_OPTIONS}
              value={filters.restriction ? [filters.restriction] : []}
              onChange={(values) => {
                const newValue = values.length > 0 ? values[values.length - 1] : undefined;
                setFilters({ ...filters, restriction: newValue });
              }}
              placeholder="Restrictions"
              enableSearch
              showClearOption
              size="sm"
              className="min-w-[160px]"
            />
            <MultiSelect
              options={MILEAGE_BAND_OPTIONS}
              value={filters.mileage_band !== undefined ? [String(filters.mileage_band)] : []}
              onChange={(values) => setFilters({ ...filters,
                mileage_band: values.length ? Number(values[values.length - 1]) : undefined,
              })}
              placeholder="Mileage Band"
              showClearOption
              size="sm"
              className="min-w-[160px]"
            />
            <MultiSelect
              options={PREPULL_FILTER_OPTIONS}
              value={filters.prepull_filter ? [filters.prepull_filter] : []}
              onChange={(values) => {
                const newValue = values.length > 0 ? values[values.length - 1] : undefined;
                setFilters({ ...filters, prepull_filter: newValue });
              }}
              placeholder="Prepulls"
              showClearOption
              size="sm"
              className="min-w-[160px]"
            />
          </div>
          {/* Right side - Driver Availability Widget */}
          <div className="ml-auto flex items-center gap-4 pl-4">
            <DriverAvailabilityWidget terminal={filters.terminal?.length === 1 ? filters.terminal[0] : undefined} />
            <button
              type="button"
              onClick={() => setShowMarketConditions(true)}
              aria-haspopup="dialog"
              className="shrink-0 rounded text-sm font-medium text-blue-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-400"
            >
              Market Conditions
            </button>
          </div>
        </div>

      {/* Main Content */}
      <div className="flex flex-col flex-1 min-h-0 p-4">
        <TabBar
          tabs={[
            { id: 'scheduler', label: 'Scheduler', current: activeTab === 'scheduler' },
            { id: 'bobtails', label: 'Bobtails', current: activeTab === 'bobtails' },
            { id: 'capacity-requests', label: 'Capacity Requests', current: activeTab === 'capacity-requests' },
            { id: 'reservations', label: 'Reservations', current: activeTab === 'reservations' },
          ]}
          onTabChange={setActiveTab}
          compact
          noBorder
          className="relative z-10 -mb-px shrink-0 [&>div:last-child]:border-b-0"
        />
        <div className="flex-1 min-h-0 min-w-0 rounded-lg border border-gray-300 bg-gray-100 p-4 dark:border-gray-600 dark:bg-gray-700">
          {/* Keep both tables mounted so switching tabs preserves edits and table state. */}
          <div role="region" aria-label="Scheduler" hidden={activeTab !== 'scheduler'} className="h-full overflow-hidden">
            <div className="flex h-full min-h-0 flex-col">
              {error && (
                <div className="mb-4 shrink-0 p-4 text-sm text-red-800 bg-red-100 rounded-lg dark:bg-red-900/20 dark:text-red-400">
                  {error}
                </div>
              )}
              <div className="min-h-0 flex-1">
                <TanTable
                  fillContainer
                  data={groupedLegs}
                  columns={columns}
                  availableFields={availableFields}
                  additionalFields={fieldSelection.additionalFields}
                  onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
                  settingsKey="scheduler"
                  trackViewedColumnId="pronumber"
                  loading={loading}
                  cardTitle={tableTitle}
                  renderExpandedContent={renderExpandedContent}
                  lockedColumns={['select', { id: 'schedule_status', side: 'right' }]}
                  getRowId={(row) => String(row.pronumber)}
                  getRowClassName={(row) => getSchedulingRowClassName(row, newPronumbers)}
                  enablePagination
                  globalPageSize={50}
                  enableColumnFilters
                />
              </div>
            </div>
          </div>
          <div role="region" aria-label="Bobtails" hidden={activeTab !== 'bobtails'} className="h-full overflow-hidden">
            <BobtailReturnsPanel mode="scheduler" filters={filters} onOpenOrder={openOrderModal} />
          </div>
          <div role="region" aria-label="Capacity Requests" hidden={activeTab !== 'capacity-requests'} className="h-full overflow-auto">
            <CapacityBoard onOpenOrder={openOrderModal} />
          </div>
          <div role="region" aria-label="Reservations" hidden={activeTab !== 'reservations'} className="h-full overflow-auto">
            <h2 className="text-lg font-semibold text-heading">Reservations</h2>
            <p className="mt-2 text-sm text-muted">Reservation functionality is coming soon.</p>
          </div>
        </div>
      </div>

      {/* Order Details Drawer */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isModalOpen && selectedPro !== null}
        onClose={closeOrderModal}
      />

      {/* Send to LC Modal */}
      <SendToLCModal
        isOpen={showSendToLCModal}
        onClose={() => {
          if (!sendingToLC) {
            setShowSendToLCModal(false);
            setLegsToSendToLC([]);
          }
        }}
        onConfirm={handleSendToLC}
        selectedCount={legsToSendToLC.length}
        legs={legsToSendToLC}
        isLoading={sendingToLC}
      />

      {/* Finalize Modal */}
      <FinalizeModal
        isOpen={showFinalizeModal}
        onClose={() => {
          if (!finalizing) {
            setShowFinalizeModal(false);
            setLegsToFinalize([]);
          }
        }}
        onConfirm={confirmFinalize}
        selectedCount={legsToFinalize.length}
        legs={legsToFinalize}
        isLoading={finalizing}
      />

      {/* Terminal Settings Modal */}
      <MarketConditionsDrawer
        isOpen={showMarketConditions}
        onClose={() => setShowMarketConditions(false)}
      />

      <TerminalSettingsModal
        isOpen={showTerminalSettingsModal}
        onClose={() => setShowTerminalSettingsModal(false)}
      />

      {/* Scheduling History Drawer */}
      <SchedulingHistoryDrawer
        isOpen={historyDrawer.open}
        onClose={() => setHistoryDrawer({ open: false, pronumber: null })}
        initialPronumber={historyDrawer.pronumber ?? undefined}
      />
    </div>
    </>
  );
}

export default function SchedulerV1() {
  return (
    <ViewedProsProvider>
      <SchedulerV1Content />
    </ViewedProsProvider>
  );
}
